import { createUseStyles } from 'react-jss';
import color from 'color';

import { ThemeProps } from '@styles/theme';

import {
    FONT_SECONDARY, RADIUS, SPACING_BASE,
} from '@styles/constants';
import { customScroll, flex } from '@resources/mixins';

export const getCustomStyles = (themeColors: ThemeProps['colors']) => ({
    menu: (provided: object) => ({
        ...provided,
        boxShadow: 'none',
        marginTop: 1,
        border: `1.5px solid ${themeColors.violet_primary}`,
        backgroundColor: themeColors.white,

        '& > div': {
            maxHeight: 400,
            ...customScroll(),
        },
    }),
    singleValue: (provided: object) => ({
        ...provided,
        fontSize: 16,
        color: themeColors.black,
    }),
    multiValue: (provided: object) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
        borderRadius: RADIUS,
        height: 28,
        margin: SPACING_BASE,
        backgroundColor: color(themeColors.violet_primary).fade(0.92).string(),
        maxWidth: 'calc(100% - 10px)',
    }),
    multiValueLabel: (provided: object) => ({
        ...provided,
        fontFamily: FONT_SECONDARY,
        lineHeight: 1.25,
        color: themeColors.violet_primary,
        fontSize: 16,
    }),
    indicatorsContainer: (provided: object) => ({
        ...provided,
        alignItems: 'flex-start',
    }),
    dropdownIndicator: (provided: object, state: Record<string, any>) => ({
        ...provided,
        color: themeColors.violet_primary,
        transition: 'transform .2s ease',
        transformOrigin: 'center',
        transform: state.selectProps.menuIsOpen ? 'scale(1, -1)' : 'scale(1, 1)',
    }),
    indicatorSeparator: (provided: object) => ({
        ...provided,
        display: 'none',
    }),
    control: (provided: object, state: Record<string, any>) => {
        const { error } = state.selectProps;

        return ({
            ...provided,
            borderWidth: 1.5,
            borderColor: error
                ? themeColors.red
                : state.isFocused
                    ? themeColors.violet_primary
                    : themeColors.grey_light,
            backgroundColor: themeColors.white,
            boxShadow: 'none',
            maxHeight: 150,
            ...customScroll(),

            '&:hover': {
                borderColor: `${color(themeColors.violet_primary).fade(0.62)}`,
            },
        })
    },
    option: (provided: object, state: Record<string, any>) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: state.isSelected
            ? color(themeColors.violet_primary).fade(0.85).string()
            : state.isFocused
                ? color(themeColors.violet_primary).fade(0.92).string()
                : themeColors.white,
        color: themeColors.black,
    }),
    input: (provided: object) => ({
        ...provided,
        color: themeColors.black,
    }),
});

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    select: {
        flex: 1,
        minWidth: 0,
        '&:focus': {
            outline: 'none',
        },
    },
    wrapper: {
        position: 'relative',
    },
    labelWithError: {
        color: theme.colors.red,
    },
    errorMessage: {
        color: theme.colors.red,
        fontSize: 14,
        position: 'absolute',
        width: '100%',
        left: 0,
        bottom: -20,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    valueRemove: {
        height: '100%',
        color: theme.colors.violet_primary,
        marginLeft: theme.spacing(0.5),
        padding: theme.spacing(0.5),
        ...flex('row', 'center', 'center'),

        '&:hover': {
            cursor: 'pointer',
            backgroundColor: 'inherit',
        },
    },
    valueRemoveIcon: {
        fontSize: 'inherit !important',
    },
    buttonContainer: {
        flexShrink: 0,
        [theme.down('mobile')]: {
            '& button': {
                width: '100%',
            },
        },
    },
    button: {
        color: theme.colors.violet_primary,
    },
    procedureLabelContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: '0 0 38%',
        minWidth: 0,
        overflow: 'hidden',
        [theme.down('mobile')]: {
            flex: '1 1 100%',
        },
    },
    procedureThumb: {
        width: 28,
        height: 28,
        borderRadius: 4,
        flexShrink: 0,
        backgroundColor: theme.colors.grey_light,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        color: theme.colors.violet_primary,
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 3,
        },
        '& svg': {
            fontSize: '16px !important',
        },
    },
    procedureLabel: {
        fontSize: 13,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: 1.3,
    },
    removeBtn: {
        width: 26,
        height: 26,
        borderRadius: '50%',
        border: `1px solid ${theme.colors.grey_light}`,
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: theme.colors.grey_text,
        padding: 0,
        flexShrink: 0,
        transition: 'all 0.15s ease',
        '&:hover': {
            background: color(theme.colors.red).fade(0.88).string(),
            borderColor: theme.colors.red,
            color: theme.colors.red,
        },
        '& svg': {
            fontSize: '14px !important',
        },
    },
    unassignedProcedureContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        marginBottom: 6,
        borderRadius: 8,
        background: color(theme.colors.red).fade(0.97).string(),
        border: `1px solid ${color(theme.colors.red).fade(0.7).string()}`,
        borderLeft: `3px solid ${theme.colors.red}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        [theme.down('mobile')]: {
            flexWrap: 'wrap',
        },
    },
    assignedProcedureContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        marginBottom: 6,
        borderRadius: 8,
        background: theme.colors.white,
        border: `1px solid ${theme.colors.grey_light}`,
        borderLeft: `3px solid ${theme.colors.violet_primary}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
        [theme.down('mobile')]: {
            flexWrap: 'wrap',
        },
    },
    versionOption: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        width: '100%',
        padding: '2px 0',
    },
    versionOptionLabel: {
        fontSize: 13,
        fontWeight: 500,
        color: theme.colors.black,
        flexShrink: 0,
        minWidth: 60,
    },
    versionMeta: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        justifyContent: 'flex-end',
    },
    versionDate: {
        fontSize: 11,
        color: theme.colors.grey_text,
        whiteSpace: 'nowrap',
        marginRight: 2,
    },
    versionMetaChip: {
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.04em',
        color: theme.colors.violet_primary,
        background: color(theme.colors.violet_primary).fade(0.9).string(),
        borderRadius: 4,
        padding: '1px 5px',
        whiteSpace: 'nowrap',
        textTransform: 'uppercase' as const,
    },
    versionChips: {
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        flexShrink: 0,
    },
    assignRow: {
        display: 'flex',
        gap: 8,
        marginTop: 8,
        padding: '8px 10px',
        background: color(theme.colors.violet_primary).fade(0.96).string(),
        borderRadius: 8,
        border: `1px dashed ${color(theme.colors.violet_primary).fade(0.65).string()}`,
        alignItems: 'center',
        [theme.down('mobile')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    procedureListHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
        marginTop: 2,
    },
    assignedCount: {
        fontSize: 11,
        color: theme.colors.grey_text,
        fontFamily: theme.font.primary,
        letterSpacing: '0.02em',
    },
    sortBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 10px 3px 8px',
        borderRadius: 20,
        border: `1.5px solid ${color(theme.colors.violet_primary).fade(0.55).string()}`,
        background: 'transparent',
        color: theme.colors.violet_primary,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: theme.font.secondary,
        letterSpacing: '0.06em',
        cursor: 'pointer',
        transition: 'all 0.18s ease',
        '&:hover': {
            background: color(theme.colors.violet_primary).fade(0.92).string(),
            borderColor: theme.colors.violet_primary,
        },
        '& svg': {
            fontSize: '14px !important',
            transition: 'transform 0.2s ease',
        },
    },
    sortBtnActive: {
        background: color(theme.colors.violet_primary).fade(0.88).string(),
        borderColor: theme.colors.violet_primary,
        '& svg': {
            transform: 'scaleY(-1)',
        },
    },
    procedureOption: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        width: '100%',
        minWidth: 0,
        gap: 8,
        padding: '2px 0',
    },
    procedureOptionName: {
        fontSize: 13,
        color: theme.colors.black,
        flex: 1,
        minWidth: 0,
        lineHeight: 1.4,
        wordBreak: 'break-word' as const,
    },
    procedureOptionMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
        marginLeft: 'auto',
    },
    procedureTypeChip: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        borderRadius: 4,
        padding: '2px 6px',
        whiteSpace: 'nowrap',
        textTransform: 'uppercase' as const,
    },
    procedureTypeChipVT: {
        color: theme.colors.violet_primary,
        background: color(theme.colors.violet_primary).fade(0.88).string(),
    },
    procedureTypeChipOpS: {
        color: theme.colors.blue_primary,
        background: color(theme.colors.blue_primary).fade(0.88).string(),
    },
    procedureTypeChipDefault: {
        color: theme.colors.grey_text,
        background: color(theme.colors.grey_light).fade(0.3).string(),
    },
    procedurePlatformIcon: {
        width: 14,
        height: 14,
        objectFit: 'contain' as const,
        display: 'block',
        flexShrink: 0,
    },
}));
